/**
 * Destructive-change warnings raised while editing a saved product, and the record of which ones
 * the user has already accepted.
 *
 * Each warning describes a consequence of moving the product off a field it was saved with —
 * grading groups dropped, DAM wiped, BOM and measurements rebuilt. The removals themselves are
 * staged and only committed when the product is saved, so the consequence is the same however many
 * times the field is changed afterwards. Accepting a warning therefore settles it for the rest of
 * the edit session: picking a different value, or clearing the field, applies straight away rather
 * than asking again.
 *
 * Cancelling never records anything, so a dismissed warning is raised again on the next attempt.
 */
export const CHANGE_WARNING = {
  /** Sub-brand or category moved, which can push the product outside a grading group it carries. */
  GRADING_SCOPE: 'gradingScope',
  /** Brand moved, orphaning grading groups that do not belong to the new brand. */
  BRAND_GRADING_GROUPS: 'brandGradingGroups',
  /** Brand or product type moved, invalidating the product's DAM assets. */
  DAM: 'dam',
  /** Product type changed or cleared, which rebuilds BOM, measurements, grading and attributes. */
  PRODUCT_TYPE: 'productType',
  /** Size range, size, colourway, Variable 1 or Variable 2 moved off values saved identifier rows use. */
  PRODUCT_IDENTIFIER: 'productIdentifier',
} as const;

export type ChangeWarning = (typeof CHANGE_WARNING)[keyof typeof CHANGE_WARNING];

/** Which warnings have been accepted so far in this edit session. */
export type AcknowledgedChangeWarnings = Partial<Record<ChangeWarning, boolean>>;

/** Nothing accepted yet. */
export const NO_ACKNOWLEDGED_WARNINGS: AcknowledgedChangeWarnings = {};

/** The fields whose change can push a product outside a grading group it already carries. */
export const GRADING_SCOPE_KEYS = ['subBrandId', 'catOneId', 'catTwoId', 'catThreeId'];

/**
 * Reports whether a warning has already been accepted.
 * @param acknowledged The record of accepted warnings.
 * @param warning The warning to look up.
 * @returns True once the user has confirmed it.
 */
export const hasAcknowledgedWarning = (acknowledged: AcknowledgedChangeWarnings, warning: ChangeWarning): boolean =>
  acknowledged[warning] === true;

/**
 * Records that the user accepted a warning.
 * @param acknowledged The record of accepted warnings.
 * @param warning The warning that was confirmed.
 * @returns A new record including the acceptance.
 */
export const acknowledgeWarning = (
  acknowledged: AcknowledgedChangeWarnings,
  warning: ChangeWarning,
): AcknowledgedChangeWarnings => ({ ...acknowledged, [warning]: true });

/**
 * Reports whether changing this field on a saved product could push it outside a grading group it
 * already carries. Brand is excluded: its own handler runs a dedicated compatibility check first.
 * @param key The field being changed.
 * @param nextValue The value being selected, or null when the field is being cleared.
 * @param product The product as currently edited.
 * @param isProductEdit Whether the form holds a saved product's grading groups: an edit, or a
 *   duplicate prefilled from one. A product created from scratch carries none to lose.
 * @returns True when the change must be held for confirmation.
 */
export const shouldConfirmGradingScopeChange = (
  key: string,
  nextValue: { _id?: string } | null | undefined,
  product: Record<string, any> | null | undefined,
  isProductEdit: boolean,
): boolean => {
  if (!GRADING_SCOPE_KEYS.includes(key)) return false;
  if (!isProductEdit || !product?.gradingGroups?.length) return false;
  const currentValue = product?.[key]?._id || product?.[key];
  return String(currentValue ?? '') !== String(nextValue?._id ?? '');
};
